import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Callout, Card, Heading, Text } from '../ui/index.js';
import type { ApiKeySummary, CreatedApiKeyResponse } from '@goodbuild/shared';
import { api } from '../api/client.js';
import { keys, useAccess } from '../api/hooks.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { FocusableTextField } from '../components/focus/OnScreenKeyboard.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { formatDate, formatDateTime, formatRelative } from '../lib/format.js';
import { errorMessage } from '../lib/errors.js';

export const Route = createFileRoute('/admin/api-keys')({ component: AdminApiKeys });

/**
 * API keys: the credential gbcli, CI and USourceControl upload with.
 *
 * The screen is built around the one fact that shapes everything else — a key is shown ONCE, when
 * it is created, because only its hash is stored. So the new-key panel is loud, sticky, and the
 * only thing on screen that can be copied; and rotation is a first-class button rather than
 * something you do by deleting and re-adding, since "I've lost it" is the common case, not an
 * unusual one.
 */
function AdminApiKeys() {
  const qc = useQueryClient();
  const { isAdmin } = useAccess();
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  /** The secret, held only in this component's state until the admin navigates away. */
  const [revealed, setRevealed] = React.useState<CreatedApiKeyResponse | null>(null);
  const [copied, setCopied] = React.useState(false);

  const list = useQuery({
    queryKey: keys.admin.apiKeys,
    queryFn: async () => (await api.get<{ apiKeys: ApiKeySummary[] }>('/admin/api-keys')).apiKeys,
    enabled: isAdmin,
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: keys.admin.apiKeys });

  function show(created: CreatedApiKeyResponse) {
    setRevealed(created);
    setCopied(false);
    setError(null);
    refresh();
  }

  const create = useMutation({
    mutationFn: () => api.post<CreatedApiKeyResponse>('/admin/api-keys', { name: name.trim() }),
    onSuccess: (created) => {
      setName('');
      show(created);
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const rotate = useMutation({
    mutationFn: (id: string) => api.post<CreatedApiKeyResponse>(`/admin/api-keys/${id}/rotate`),
    onSuccess: show,
    onError: (err) => setError(errorMessage(err)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.post(`/admin/api-keys/${id}/revoke`),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  async function copy(key: string) {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the key is on screen and selectable either way.
      setCopied(false);
    }
  }

  if (!isAdmin) return <EmptyState title={'You cannot do that.'} />;
  if (list.isLoading) return <Loading />;

  const rows = list.data ?? [];
  const live = rows.filter((row) => !row.revokedAt);

  return (
    <div className="gb-stack">
      {error ? (
        <Callout.Root color="danger">
          <Callout.Description>{error}</Callout.Description>
        </Callout.Root>
      ) : null}

      {/* Shown once and never again. Deliberately the most prominent thing on the page. */}
      {revealed ? (
        <Card size="3">
          <Heading size="4">{'Copy this key now'}</Heading>
          <Text render={<p />} size="2" color="gray">
            {
              'This is the only time it can be shown. Only a hash of it is stored, so if you lose it, rotate the key to get a new one.'
            }
          </Text>
          <pre className="gb-key" aria-label="New API key">
            <code>{revealed.key}</code>
          </pre>
          <div className="gb-row">
            <Focusable onSelect={() => void copy(revealed.key)} selectLabel={'Copy'}>
              <Button size="2">{copied ? 'Copied' : 'Copy'}</Button>
            </Focusable>
            <Focusable onSelect={() => setRevealed(null)} selectLabel={'Done'}>
              <Button size="2" variant="ghost">
                {'Done'}
              </Button>
            </Focusable>
          </div>
        </Card>
      ) : null}

      <Card size="3">
        <Heading size="4">{'New API key'}</Heading>
        <Text render={<p />} size="1" color="gray">
          {
            'A key uploads as you and can do anything your account can. Name it after the machine that will hold it, so revoking the right one later is obvious.'
          }
        </Text>
        <FocusableTextField
          value={name}
          onChange={setName}
          label={'Name'}
          maxLength={64}
          autoFocus
        />
        <div style={{ marginTop: '0.5rem' }}>
          <Focusable
            onSelect={() => create.mutate()}
            selectLabel={'Create'}
            disabled={!name.trim() || create.isPending}
          >
            <Button size="2">{'Create key'}</Button>
          </Focusable>
        </div>
      </Card>

      <FocusZone focusKey="admin-api-keys" direction="column" className="gb-stack">
        {rows.length === 0 ? (
          <EmptyState title={'No keys yet'} />
        ) : (
          rows.map((row) => (
            <Card key={row.id} size="2">
              <div className="gb-seat">
                <Text size="2">{row.name}</Text>
                <code className="gb-key__hint">{`${row.prefix}…`}</code>
                {row.revokedAt ? <Badge color="amber">{'Revoked'}</Badge> : null}
                {row.rotatedFrom && !row.revokedAt ? <Badge>{'Rotated'}</Badge> : null}
                <span className="gb-seat__spacer" />
                <Text size="1" color="gray">
                  {'Created'}: {formatDate(row.createdAt)}
                </Text>
                <Text size="1" color="gray">
                  {/* "Never used" is the useful reading of a null here: it is how you spot a key
                      that was created for something that never shipped. */}
                  {row.lastUsedAt ? `Last used ${formatRelative(row.lastUsedAt)}` : 'Never used'}
                </Text>
                {!row.revokedAt ? (
                  <>
                    <Focusable
                      onSelect={() => rotate.mutate(row.id)}
                      selectLabel={'Rotate'}
                      disabled={rotate.isPending}
                    >
                      <Button size="1" variant="ghost">
                        {'Rotate'}
                      </Button>
                    </Focusable>
                    <Focusable
                      onSelect={() => revoke.mutate(row.id)}
                      selectLabel={'Revoke'}
                      disabled={revoke.isPending}
                    >
                      <Button size="1" variant="ghost" color="danger">
                        {'Revoke'}
                      </Button>
                    </Focusable>
                  </>
                ) : (
                  <Text size="1" color="gray">
                    {formatDateTime(row.revokedAt)}
                  </Text>
                )}
              </div>
            </Card>
          ))
        )}
      </FocusZone>

      {live.length > 0 ? (
        <Text render={<p />} size="1" color="gray">
          {
            'Give a key to gbcli as GBCLI_TOKEN, or paste it into USourceControl under Releases → GOOD BUILD. Rotating replaces a key and stops the old one immediately, so change it everywhere it is used.'
          }
        </Text>
      ) : null}
    </div>
  );
}
